import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Divider,
  Switch,
  FormControlLabel,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';

export default function EditBotDialog({ open, onClose, onSuccess, bot }) {
  const [formData, setFormData] = useState({
    username: '',
    token: '',
    botType: 'personal',
    settings: {
      greeting: {
        enabled: false,
        message: '',
      },
      commands: [],
    },
  });

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (bot) {
      setFormData({
        username: bot.username,
        token: bot.token,
        botType: bot.botType || 'personal',
        settings: {
          ...bot.settings,
          greeting: bot.settings?.greeting || { enabled: false, message: '' },
          commands: bot.settings?.commands || [],
        },
      });
    }
  }, [bot]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSettingsChange = (path, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [path]: value,
      },
    }));
  };

  const handleCommandChange = (index, field, value) => {
    const newCommands = [...formData.settings.commands];
    newCommands[index] = {
      ...newCommands[index],
      [field]: value,
    };
    handleSettingsChange('commands', newCommands);
  };

  const handleAddCommand = () => {
    handleSettingsChange('commands', [
      ...formData.settings.commands,
      { command: '', description: '', isEnabled: true },
    ]);
  };

  const handleRemoveCommand = (index) => {
    const newCommands = formData.settings.commands.filter((_, i) => i !== index);
    handleSettingsChange('commands', newCommands);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/telegram-bots/${bot._id}`, formData);
      enqueueSnackbar('Бот успешно обновлен', { variant: 'success' });
      onSuccess();
    } catch (error) {
      enqueueSnackbar(
        error.response?.data?.message || 'Ошибка при обновлении бота',
        { variant: 'error' }
      );
    }
  };

  if (!bot) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редактировать бота</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="username"
            label="Имя пользователя бота"
            type="text"
            fullWidth
            value={formData.username}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="token"
            label="Токен бота"
            type="text"
            fullWidth
            value={formData.token}
            onChange={handleChange}
            required
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth margin="dense" sx={{ mb: 3 }}>
            <InputLabel id="edit-bot-type-label">Тип бота</InputLabel>
            <Select
              labelId="edit-bot-type-label"
              name="botType"
              value={formData.botType}
              onChange={handleChange}
              label="Тип бота"
            >
              <MenuItem value="public">Публичный</MenuItem>
              <MenuItem value="personal">Личный</MenuItem>
              <MenuItem value="private">Приватный</MenuItem>
            </Select>
          </FormControl>
          {formData.botType === 'private' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Приватный бот доступен только пользователям из списка доступа. Пустой список запрещает доступ всем. Сохраните тип бота, затем нажмите «Доступ» в списке ботов. Изменения доступа сохраняются отдельно и сразу, независимо от этой формы.
            </Alert>
          )}

        <Typography variant="h6" sx={{ mb: 2, backgroundColor: "rgba(255, 0, 0, 0.2)" }}>
            Настройки приветствия (Данный блок больше не функционирует и будет удален!)
          </Typography>
          <Box sx={{ mb: 3, backgroundColor: "rgba(255, 0, 0, 0.2)" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.settings.greeting.enabled}
                  onChange={(e) =>
                    handleSettingsChange('greeting', {
                      ...formData.settings.greeting,
                      enabled: e.target.checked,
                    })
                  }
                />
              }
              label="Включить приветственное сообщение"
            />
            {formData.settings.greeting.enabled && (
              <TextField
                fullWidth
                multiline
                rows={2}
                value={formData.settings.greeting.message}
                onChange={(e) =>
                  handleSettingsChange('greeting', {
                    ...formData.settings.greeting,
                    message: e.target.value,
                  })
                }
                label="Текст приветствия"
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Команды бота</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddCommand}>
              Добавить команду
            </Button>
          </Box>

          {formData.settings.commands.map((command, index) => (
            <Box key={index} sx={{ mb: 2, display: 'flex',justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
              <Box sx={{ display: 'flex',flexDirection: 'column', gap: 2, width: '70%' }}>
              <TextField
                size="small"
                label="Команда"
                value={command.command}
                onChange={(e) => handleCommandChange(index, 'command', e.target.value)}
                sx={{ width: '30%' }}
              />
              <TextField
                size="small"
                label="Описание"
                value={command.description}
                onChange={(e) => handleCommandChange(index, 'description', e.target.value)}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Сообщение"
                value={command.message}
                onChange={(e) => handleCommandChange(index, 'message', e.target.value)}
                sx={{ flex: 1 }}
                multiline
                rows={4}
              />
              </Box>
              <Box>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={command.isEnabled}
                    onChange={(e) => handleCommandChange(index, 'isEnabled', e.target.checked)}
                  />
                }
                label="Активна"
              />
              <IconButton
                size="small"
                color="error"
                onClick={() => handleRemoveCommand(index)}
              >
                <DeleteIcon />
              </IconButton>
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отмена</Button>
          <Button type="submit" variant="contained">
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
